import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import config from '../config';

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token and user/tenant info
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('tenant', JSON.stringify(data.tenant));

      // Check if password change is required
      if (data.mustChangePassword) {
        localStorage.setItem('mustChangePassword', 'true');
      }

      onLogin(data.token);
    } catch (err) {
      setError(err.message || 'Failed to login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        py: 4
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
          }}
        >
          {/* Logo and Header */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/PhysicanslifelineLogo.png"
              alt="Physicians Lifeline"
              sx={{
                height: 60,
                mb: 3,
                objectFit: 'contain'
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontWeight: 400,
                fontSize: '0.875rem'
              }}
            >
              Sign in to access your dashboard
            </Typography>
          </Box>

          {/* Error Message */}
          {error && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 1,
                color: '#991b1b',
                fontSize: '0.875rem'
              }}
            >
              {error}
            </Box>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 0.5,
                  color: '#374151',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                Username
              </Typography>
              <TextField
                required
                fullWidth
                placeholder="Enter your username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <PersonOutlineIcon sx={{ color: '#9ca3af', mr: 1, fontSize: 20 }} />
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                    },
                    '&:hover fieldset': {
                      borderColor: '#d1d5db',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    py: 1.5,
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 0.5,
                  color: '#374151',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                Password
              </Typography>
              <TextField
                required
                fullWidth
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <LockOutlinedIcon sx={{ color: '#9ca3af', mr: 1, fontSize: 20 }} />
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                    },
                    '&:hover fieldset': {
                      borderColor: '#d1d5db',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    py: 1.5,
                  }
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.5,
                bgcolor: '#3b82f6',
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#2563eb',
                  boxShadow: 'none',
                },
                '&:active': {
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: '#93c5fd',
                  color: 'white'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Footer */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography
              variant="caption"
              sx={{
                color: '#9ca3af',
                fontSize: '0.75rem'
              }}
            >
              © 2025 Physicians Lifeline • v1.0
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;
